import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { getClientId } from '@/utils/clientId';
import { useUnlockMutation, useWaiterPinPolicyQuery } from './waiterApi';
import NameTakeoverPrompt, { ActiveDevice } from './NameTakeoverPrompt';
import RestaurantMark from './RestaurantMark';
import { setWaiterAuth } from './waiterSlice';

// A restaurant configures either a 4- or a 6-digit PIN (sale_api
// WaiterModeConfigWriteSerializer.validate_pin). SC-776: which one it is comes
// from `pin-policy/`; these two only bound the pad while that answer is
// missing.
const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 6;
const GRID_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

interface UnlockErrorShape {
  status?: number | string;
  data?: {
    code?: string;
    active_devices?: ActiveDevice[];
    /** SC-576 WAITER_NAME_IN_USE — the name already live on another device. */
    waiter_name?: string;
    /** SC-379 PIN lockout — ISO datetime the block lifts + seconds remaining. */
    locked_until?: string;
    retry_after?: number;
  };
}

const LOCKOUT_FALLBACK_SECONDS = 15 * 60;

interface PinPadProps {
  restaurantUuid: string;
  /** SC-979 — l'écran de code est le même pour tous les restaurants : sa
   *  ligne de titre porte donc la marque de celui-ci, à la place du
   *  « Code d'accès » générique (le pavé et les points le disent déjà). */
  restaurantName?: string;
  restaurantLogoUrl?: string | null;
  restaurantLogoInverseUrl?: string | null;
  /** Existing picked name, if any — carried through an idle-lock re-unlock so
   * the waiter isn't sent back to the name picker after a routine re-lock
   * (spec §2.3: re-unlock keeps the same device_id, doesn't double-count). */
  knownWaiterName?: string;
  /** Hands the just-validated PIN up so NamePicker can re-unlock with a name
   * moments later — the name isn't known yet at PIN-entry time, so the first
   * unlock call never carries it (spec §2.3: name switch = re-PIN). */
  onUnlocked?: (pin: string) => void;
  /** SC-761 — set when this lock screen is the second half of a « changer de
   *  serveur » started from the notes list: the name was already picked in
   *  the identity sheet, so say whose code is being asked for instead of
   *  showing a bare pad. `knownWaiterName` already carries the same value —
   *  this only drives the copy and the way out. */
  switchingTo?: string | null;
  /** Backs out of that switch: stays locked, drops the pending name. */
  onCancelSwitch?: () => void;
}

export default function PinPad({
  restaurantUuid,
  restaurantName,
  restaurantLogoUrl,
  restaurantLogoInverseUrl,
  knownWaiterName,
  onUnlocked,
  switchingTo = null,
  onCancelSwitch,
}: PinPadProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [unlock, { isLoading }] = useUnlockMutation();
  // SC-776 — the configured PIN length, so the pad draws exactly that many
  // dots and verifies only once they are all typed. `null` while the call is
  // in flight, if it fails, or if no 4/6-digit PIN is configured: the pad then
  // keeps its legacy behaviour (6 dots, probe at 4 then again at 6), which is
  // the only way to submit a 4-digit PIN without knowing the length.
  const { data: pinPolicy } = useWaiterPinPolicyQuery(restaurantUuid, {
    skip: !restaurantUuid,
    // The restaurant can change its PIN (and its length) from admin/owner
    // between two lock screens — re-read it every time the pad comes up.
    refetchOnMountOrArgChange: true,
  });
  const pinLength = pinPolicy?.pinLength ?? null;
  const padLength = pinLength ?? PIN_MAX_LENGTH;
  const [digits, setDigits] = useState('');
  const [shaking, setShaking] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [capDevices, setCapDevices] = useState<ActiveDevice[] | null>(null);
  // SC-576: the known name this device re-PINs with is already serving on
  // another device — hold the confirmation screen until the waiter accepts
  // signing that device out (or cancels).
  const [nameConflict, setNameConflict] = useState<ActiveDevice[] | null>(null);
  // SC-379: epoch-ms when the PIN lockout lifts (null = not locked). `nowTs`
  // ticks each second while locked so the countdown re-renders and the pad
  // auto-re-enables when it elapses.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());

  const submit = async (
    pin: string,
    takeoverDeviceUuid?: string,
    forceNameTakeover?: boolean,
  ) => {
    setErrorKey(null);
    try {
      const result = await unlock({
        restaurantUuid,
        pin,
        deviceId: getClientId(),
        deviceLabel: navigator.userAgent.slice(0, 80),
        waiterName: knownWaiterName || undefined,
        takeoverDeviceUuid,
        forceNameTakeover,
      }).unwrap();

      setCapDevices(null);
      setNameConflict(null);
      setDigits('');
      dispatch(
        setWaiterAuth({
          restaurantUuid,
          token: result.token,
          deviceUuid: result.deviceUuid,
          waiterName: knownWaiterName || '',
          waiterNames: result.waiterNames,
          idleLockSeconds: result.idleLockSeconds,
          expiresAt: result.expiresAt,
        }),
      );
      onUnlocked?.(pin);
    } catch (err: unknown) {
      const apiErr = err as UnlockErrorShape;
      const code = apiErr.data?.code;

      if (apiErr.status === 429 && code === 'PIN_LOCKED') {
        const until = apiErr.data?.locked_until
          ? new Date(apiErr.data.locked_until).getTime()
          : Date.now() + (apiErr.data?.retry_after ?? LOCKOUT_FALLBACK_SECONDS) * 1000;
        setLockedUntil(until);
        setNowTs(Date.now());
        setErrorKey(null);
        // SC-434: if a takeover attempt trips the lockout, leave the cap screen so
        // the lockout countdown is actually shown (the cap screen has no countdown).
        setCapDevices(null);
        setNameConflict(null);
        setDigits('');
        return;
      }

      if (apiErr.status === 409 && code === 'WAITER_NAME_IN_USE') {
        setNameConflict(apiErr.data?.active_devices ?? []);
        return;
      }
      if (apiErr.status === 409 && code === 'DEVICE_CAP') {
        setCapDevices(apiErr.data?.active_devices ?? []);
        return;
      }
      if (
        apiErr.status === 403 &&
        code === 'BAD_PIN' &&
        pinLength === null &&
        pin.length < PIN_MAX_LENGTH
      ) {
        // Legacy probe only (SC-776: unreachable once the length is known) —
        // first (4-digit) guess against a possibly 6-digit PIN, so let the
        // waiter keep typing instead of resetting.
        setErrorKey('waiter.pinPad.errorBadPin');
        return;
      }
      setErrorKey(
        apiErr.status === 403 && code === 'WAITER_MODE_DISABLED'
          ? 'waiter.pinPad.errorDisabled'
          : apiErr.status === 403 && code === 'BAD_PIN'
            ? 'waiter.pinPad.errorBadPin'
            : 'waiter.pinPad.errorGeneric',
      );
      // SC-434: on the device-cap screen a failed "Remplacer" (takeover) must keep
      // the PIN. Clearing it made every retry submit an empty PIN that failed
      // silently, so the button appeared to do nothing. The surfaced error is
      // rendered on the cap screen (below), and the same PIN can be retried.
      // Same reasoning for the SC-576 name-takeover confirmation screen.
      if (!capDevices && !nameConflict) setDigits('');
      setShaking(true);
    }
  };

  useEffect(() => {
    if (pinLength === null) {
      // Length unknown — probe at 4, then again at 6.
      if (digits.length === PIN_MIN_LENGTH || digits.length === PIN_MAX_LENGTH) {
        void submit(digits);
      }
    } else if (digits.length === pinLength) {
      void submit(digits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  useEffect(() => {
    if (!shaking) return;
    const id = setTimeout(() => setShaking(false), 300);
    return () => clearTimeout(id);
  }, [shaking]);

  useEffect(() => {
    if (lockedUntil === null) return;
    const id = setInterval(() => {
      if (Date.now() >= lockedUntil) {
        setLockedUntil(null);
      } else {
        setNowTs(Date.now());
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const remainingSec =
    lockedUntil !== null ? Math.max(0, Math.ceil((lockedUntil - nowTs) / 1000)) : 0;
  const locked = remainingSec > 0;
  const countdown = `${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, '0')}`;

  const handleDigit = (d: string) => {
    if (isLoading || locked || digits.length >= padLength) return;
    setErrorKey(null);
    setDigits((prev) => prev + d);
  };

  const handleBackspace = () => {
    if (isLoading || locked || digits.length === 0) return;
    setDigits((prev) => prev.slice(0, -1));
  };

  if (nameConflict) {
    return (
      <NameTakeoverPrompt
        waiterName={knownWaiterName || ''}
        devices={nameConflict}
        busy={isLoading}
        error={errorKey ? t(errorKey) : null}
        onConfirm={() => void submit(digits, undefined, true)}
        onCancel={() => {
          setNameConflict(null);
          setDigits('');
        }}
      />
    );
  }

  if (capDevices) {
    return (
      <div
        className="waiter-surface min-h-[100dvh] flex flex-col items-center justify-center gap-4 p-6 bg-cream"
        data-testid="waiter-device-cap"
      >
        <h2 className="font-display text-2xl text-ink">{t('waiter.pinPad.capTitle')}</h2>
        {errorKey && (
          <p className="text-sm text-red-600" role="alert" data-testid="waiter-cap-error">
            {t(errorKey)}
          </p>
        )}
        <ul className="w-full max-w-sm flex flex-col gap-3 list-none p-0">
          {capDevices.map((device) => (
            <li
              key={device.uuid}
              className="border-2 border-ink bg-paper rounded-btn-sm px-4 py-3 flex items-center justify-between gap-3"
            >
              <div>
                <p className="font-display">
                  {device.waiter_name || device.device_label || t('waiter.pinPad.unknownDevice')}
                </p>
                <p className="text-xs text-ink/70">{device.device_label}</p>
              </div>
              <button
                type="button"
                onClick={() => void submit(digits, device.uuid)}
                disabled={isLoading}
                className="bg-ink text-paper font-display px-3 py-1.5 rounded-btn-sm disabled:opacity-50"
              >
                {t('waiter.pinPad.takeover')}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => {
            setCapDevices(null);
            setDigits('');
          }}
          className="font-display text-sm underline"
        >
          {t('common.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div
      // Short screens (a 320x568 phone, and any phone once the « Passer à X »
      // line + « Annuler » of a switch are on the pad) do not fit the 24px
      // rhythm: without this the cancel button sat below the fold.
      className="waiter-surface min-h-[100dvh] flex flex-col items-center justify-center gap-6 p-6 [@media(max-height:640px)]:gap-3 [@media(max-height:640px)]:p-4 bg-cream"
      style={{ touchAction: 'manipulation' }}
    >
      <h1
        className={`font-display text-2xl text-ink flex items-center justify-center ${
          // La marque coiffe les points du code : sans ce rapprochement elle
          // flotte a egale distance du haut de l'ecran et du pave, et ne se
          // lit plus comme le titre de ce qu'elle surmonte (revue visuelle).
          restaurantName ? '-mb-2.5 [@media(max-height:640px)]:mb-0' : ''
        }`}
      >
        {restaurantName ? (
          <RestaurantMark
            variant="plate"
            name={restaurantName}
            logoUrl={restaurantLogoUrl}
            logoInverseUrl={restaurantLogoInverseUrl}
          />
        ) : (
          t('waiter.pinPad.title')
        )}
      </h1>
      {switchingTo && (
        <p data-testid="pin-switching-to" className="font-sans text-[18px] font-semibold text-ink text-center -mt-3">
          {t('waiter.pinPad.switchingTo', { name: switchingTo })}
        </p>
      )}
      <div
        data-testid="pin-dots"
        className={`flex gap-3 ${shaking ? 'animate-[pinShake_300ms_ease-in-out]' : ''}`}
      >
        {Array.from({ length: padLength }).map((_, i) => (
          <span
            key={i}
            data-filled={i < digits.length}
            className={`w-4 h-4 rounded-full border-2 border-ink ${i < digits.length ? 'bg-ink' : 'bg-transparent'}`}
          />
        ))}
      </div>
      {locked ? (
        <p className="text-sm text-red-600 text-center max-w-xs" role="alert">
          {t('waiter.pinPad.errorLocked')}
          <span className="block mt-1 font-display tabular-nums">{countdown}</span>
        </p>
      ) : (
        errorKey && (
          <p className="text-sm text-red-600" role="alert">
            {t(errorKey)}
          </p>
        )
      )}
      <div className="grid grid-cols-3 gap-3 w-full max-w-xs [@media(max-height:640px)]:max-w-[264px]">
        {GRID_DIGITS.map((d) => (
          <button
            key={d}
            type="button"
            data-testid={`pin-key-${d}`}
            onClick={() => handleDigit(d)}
            disabled={isLoading || locked || digits.length >= padLength}
            className="aspect-square bg-paper border-2 border-ink rounded-btn font-display text-2xl disabled:opacity-50"
          >
            {d}
          </button>
        ))}
        <div />
        <button
          type="button"
          data-testid="pin-key-0"
          onClick={() => handleDigit('0')}
          disabled={isLoading || locked || digits.length >= padLength}
          className="aspect-square bg-paper border-2 border-ink rounded-btn font-display text-2xl disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          onClick={handleBackspace}
          disabled={isLoading || locked || digits.length === 0}
          aria-label={t('waiter.pinPad.backspace')}
          className="aspect-square bg-paper border-2 border-ink rounded-btn font-display text-lg disabled:opacity-50"
        >
          ⌫
        </button>
      </div>
      {switchingTo && onCancelSwitch && (
        <button
          type="button"
          data-testid="pin-cancel-switch"
          onClick={onCancelSwitch}
          className="min-h-[44px] px-4 font-sans text-base underline text-ink"
        >
          {t('waiter.pinPad.cancelSwitch')}
        </button>
      )}
      <style>{`
        @keyframes pinShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
